import { RefreshButton } from '@bumble/app/components/RefreshButton';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import {
  Box,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem
} from '@mui/material';
import { GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';
import React, { ReactElement, useState } from 'react';

interface TableActionsProps {
  itemsSelected?: boolean;
  btnLabel?: string | null;
  btnOnclick?: () => void | null;
  refreshData?: () => void | null;
  menuItems?:
  | {
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    hasDivider?: boolean;
    disabled?: boolean;
  }[]
  | null;
  secondarySlot?: React.ReactNode;
}

function TableActions({
  itemsSelected,
  btnLabel = '',
  btnOnclick,
  refreshData,
  menuItems,
  secondarySlot,
}: TableActionsProps) {
  const [actionsEl, setActionsEl] = useState<null | HTMLElement>(null);
  const openActionsMenu = Boolean(actionsEl);
  const handleActionsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setActionsEl(event.currentTarget);
  };
  const handleActionsClose = () => {
    setActionsEl(null);
  };
  const [rotateIcon, setRotateIcon] = useState(0);
  const handleRefreshClick = () => {
    setRotateIcon(rotateIcon + 1);
    if (refreshData) {
      refreshData();
    }
  };
  const menuItemsArray: ReactElement[] = [];
  menuItems?.forEach(({ label, icon, onClick, hasDivider, disabled }, index) => {
    if (hasDivider && index > 0) {
      menuItemsArray.push(<Divider key={`divider-${label}`} />);
    }
    menuItemsArray.push(
      <MenuItem
        onClick={() => {
          onClick();
          handleActionsClose();
        }}
        key={label}
        title={label}
        disabled={disabled}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText>{label}</ListItemText>
      </MenuItem>,
    );
  });

  return (
    <GridToolbarContainer sx={{ mb: 2 }}>
      <GridToolbarQuickFilter
        variant="outlined"
        size="small"
        debounceMs={250}
        sx={{ mr: 'auto' }}
      />
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        mb={2}
        gap={2}
        sx={{
          justifyContent: 'flex-end',
        }}
      >
        {refreshData && (
          <RefreshButton onRefresh={handleRefreshClick} />
        )}
        {menuItems && (
          <>
            <Button
              size="small"
              variant="outlined"
              endIcon={<KeyboardArrowDownOutlinedIcon fontSize="small" />}
              id="actions-button"
              aria-label="menu"
              aria-controls={openActionsMenu ? 'actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={openActionsMenu ? 'true' : undefined}
              onClick={handleActionsClick}
              disabled={!itemsSelected}
              title="Actions"
            >
              Actions
            </Button>
            <Menu
              elevation={1}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              id="actions-menu"
              anchorEl={actionsEl}
              open={openActionsMenu}
              onClose={handleActionsClose}
              MenuListProps={{
                'aria-labelledby': 'actions-button',
              }}
            >
              {menuItemsArray}
            </Menu>
          </>
        )}
        {btnLabel && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<AddOutlinedIcon fontSize="small" sx={{ marginRight: { md: 1, xs: 0 } }} />}
            onClick={btnOnclick}
            title="New"
          >
            <Box component="span" sx={{ display: { md: 'inline', xs: 'none' } }}>
              {btnLabel}
            </Box>
            <Box component="span" sx={{ display: { md: 'none', xs: 'inline' } }}>
              New
            </Box>
          </Button>
        )}
        {secondarySlot}
      </Box>
    </GridToolbarContainer>
  );
}

export default TableActions;
